import type { DirEntry, PlatformBridge } from '@slate/core';

export interface PathListing {
  baseDir: string;
  segment: string;
  entries: DirEntry[];
}

export interface PathProvider {
  list: (token: string) => Promise<PathListing>;
}

interface Options {
  platform: PlatformBridge;
  rootDir: string;
}

function joinPath(base: string, name: string): string {
  if (base === '/' || base === '') return '/' + name;
  return base.replace(/\/+$/, '') + '/' + name;
}

function parentOf(p: string): string {
  if (!p || p === '/') return '/';
  const trimmed = p.replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx <= 0 ? '/' : trimmed.slice(0, idx);
}

function splitToken(token: string, rootDir: string): { baseDir: string; segment: string } {
  let working = token;
  let base = rootDir;
  if (working.startsWith('/')) {
    base = '/';
    working = working.slice(1);
  }
  const lastSlash = working.lastIndexOf('/');
  if (lastSlash < 0) {
    return { baseDir: base, segment: working };
  }
  const traversed = working.slice(0, lastSlash);
  const segment = working.slice(lastSlash + 1);
  if (traversed === '') return { baseDir: base, segment };
  const parts = traversed.split('/').filter((p) => p !== '' && p !== '..' && p !== '.');
  for (const part of parts) base = joinPath(base, part);
  return { baseDir: base, segment };
}

function filterAndSort(entries: DirEntry[], segment: string): DirEntry[] {
  const seg = segment.toLowerCase();
  const dotty = seg.startsWith('.');
  return entries
    .filter((e) => dotty || !e.name.startsWith('.'))
    .filter((e) => seg === '' || e.name.toLowerCase().includes(seg))
    .sort((a, b) => {
      if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

/**
 * Path-completion state machine over `platform.readDir`, with per-instance cache.
 * Security: the platform adapter (TauriPlatform asset scope / WebPlatform granted
 * handles) is the source of truth for which directories may be read. This provider
 * strips `..` / `.` segments at the UI layer as defense-in-depth.
 */
export function createPathProvider({ platform, rootDir }: Options): PathProvider {
  const cache = new Map<string, DirEntry[]>();
  return {
    async list(token: string): Promise<PathListing> {
      const { baseDir, segment } = splitToken(token, rootDir);
      let entries = cache.get(baseDir);
      if (!entries) {
        entries = await platform.readDir(baseDir);
        cache.set(baseDir, entries);
      }
      return { baseDir, segment, entries: filterAndSort(entries, segment) };
    },
  };
}

export { joinPath, parentOf };
